import { CheckCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Empty, message, Space, Spin, Typography } from "antd";
import { AxiosError } from "axios";
import { getProfile } from "entities/viewer";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getDeviceType } from "shared/lib/utils";
import ROUTES from "shared/routes";
import { Input } from "shared/ui/Input";
import styles from "./index.module.scss";

interface SignDocumentProps {
  document: {
    id: string;
    sign_provider: string;
    edo_status?: { code: string };
  };
  localeKey: string;
  withoutGoToMain?: boolean;
  customRout?: string | null;
  setShouldPoll: Dispatch<SetStateAction<boolean>>;
  signDocumentById: (
    id: string,
    params?: { provider: string } | undefined,
  ) => Promise<{
    success: boolean;
    token: string;
    link: string;
    qrCode: string;
  }>;
  checkSignDocumentById: (
    id: string,
    params: { token: string; code: string },
  ) => Promise<{
    success: boolean;
    error?: { code: string; description: string };
  }>;
}

interface SingDocumentContentProps {
  isCheckSignDocumentPending: boolean;
  localeKey: string;
  checkSignDocumentData: { success?: boolean } | undefined;
  document: {
    id: string;
    sign_provider: string;
    edo_status?: { code: string };
  };
  signDocumentData:
    | {
        success?: boolean;
        token?: string;
        qrCode?: string;
        link?: string;
      }
    | undefined;
  signDocument: (params: { id: string; params?: { provider: string } }) => void;
  isSignDocumentPending: boolean;
  isShowQrCode: boolean;
  userData?: { phone: string };
  code: string;
  setCode: Dispatch<SetStateAction<string>>;
  isTimerActive: boolean;
  timer: number;
  checkSignDocument: (params: {
    id: string;
    params: { token: string; code: string };
  }) => void;
  signDocumentIsError: boolean;
  signDocumentError: Error | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  withoutGoToMain?: boolean;
  customRout?: string | null;
  setShouldPoll: Dispatch<SetStateAction<boolean>>;
}

export const SingDocument = ({
  document,
  localeKey,
  setShouldPoll,
  signDocumentById,
  checkSignDocumentById,
  withoutGoToMain,
  customRout,
}: SignDocumentProps) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [code, setCode] = useState<string>("");
  const [timer, setTimer] = useState<number>(60);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [isShowQrCode, setIsShowQrCode] = useState(false);

  const { data: userData } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
  });

  const {
    mutate: signDocument,
    isPending: isSignDocumentPending,
    data: signDocumentData,
    isError: signDocumentIsError,
    error: signDocumentError,
  } = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params?: { provider: string };
    }) => signDocumentById(id, params),
    onSuccess(response) {
      if (response.success) {
        setIsTimerActive(true);
        setTimer(60);

        if (
          document?.sign_provider === "SMART_BRIDGE" &&
          response.link &&
          !isShowQrCode
        ) {
          setShouldPoll(true);
          window.open(response.link, "_blank");
        }

        messageApi["success"](t(`${localeKey}.code_sent`));
      } else {
        messageApi["error"](t(`${localeKey}.error_occurred`));
      }
    },
    onError(error: AxiosError<{ message: string }>) {
      messageApi["error"](
        error.response?.data?.message || t(`${localeKey}.error_occurred`),
      );
    },
  });

  const {
    mutate: checkSignDocument,
    isPending: isCheckSignDocumentPending,
    data: checkSignDocumentData,
  } = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: {
        token: string;
        code: string;
      };
    }) => checkSignDocumentById(id, params),
    onSuccess(response) {
      if (response.success) {
        messageApi["success"](t(`${localeKey}.document_signed`));
      } else {
        setCode("");
        messageApi["error"](
          response.error?.description || t(`${localeKey}.error_occurred`),
        );
      }
    },
    onError(error: AxiosError<{ message: string }>) {
      setCode("");
      messageApi["error"](
        error.response?.data?.message || t(`${localeKey}.error_occurred`),
      );
    },
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }

    return () => clearInterval(interval);
  }, [isTimerActive, timer]);

  useEffect(() => {
    if (getDeviceType() !== "mobile") {
      setIsShowQrCode(true);
    }
  }, []);

  return (
    <Space orientation="vertical" size="middle" style={{ display: "flex" }}>
      {contextHolder}
      <Typography.Title level={5}>
        {t(`${localeKey}.sign_document`)}
      </Typography.Title>

      <SingDocumentContent
        localeKey={localeKey}
        isCheckSignDocumentPending={isCheckSignDocumentPending}
        checkSignDocumentData={checkSignDocumentData}
        document={document}
        signDocumentData={signDocumentData}
        signDocument={signDocument}
        isSignDocumentPending={isSignDocumentPending}
        isShowQrCode={isShowQrCode}
        userData={userData}
        code={code}
        setCode={setCode}
        isTimerActive={isTimerActive}
        timer={timer}
        checkSignDocument={checkSignDocument}
        signDocumentIsError={signDocumentIsError}
        signDocumentError={signDocumentError}
        t={t}
        withoutGoToMain={withoutGoToMain}
        customRout={customRout}
        setShouldPoll={setShouldPoll}
      />
    </Space>
  );
};

const SingDocumentContent = ({
  isCheckSignDocumentPending,
  checkSignDocumentData,
  document,
  signDocumentData,
  signDocument,
  isSignDocumentPending,
  isShowQrCode,
  userData,
  code,
  setCode,
  isTimerActive,
  timer,
  checkSignDocument,
  isCheckSignDocumentPending: isCheckPending,
  signDocumentIsError,
  signDocumentError,
  t,
  localeKey,
  withoutGoToMain,
  customRout,
  setShouldPoll,
}: SingDocumentContentProps) => {
  const navigate = useNavigate();

  if (
    !(!isCheckSignDocumentPending && checkSignDocumentData?.success) &&
    document.edo_status?.code !== "FINISHED" &&
    signDocumentData?.success !== true
  ) {
    return (
      <Button
        type="primary"
        block
        onClick={() =>
          signDocument({
            id: document.id,
            params:
              document.sign_provider === "SMART_BRIDGE"
                ? { provider: "SMART_BRIDGE" }
                : { provider: "SMS" },
          })
        }
        loading={isSignDocumentPending}
        style={{ marginBottom: 16 }}
      >
        {document.sign_provider === "SMART_BRIDGE"
          ? t(`${localeKey}.gov_mobile`)
          : t(`${localeKey}.get_code`)}
      </Button>
    );
  }

  if (
    (!isCheckSignDocumentPending && checkSignDocumentData?.success) ||
    document.edo_status?.code === "FINISHED"
  ) {
    return (
      <div className={styles.checkSignSuccess}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 52,
            height: 52,
            borderRadius: "50%",
            fontSize: 26,
            color: "var(--ant-green)",
            backgroundColor: "var(--ant-green-2)",
          }}
        >
          <CheckCircleOutlined />
        </div>

        <Typography.Title level={2} style={{ textAlign: "center" }}>
          {t(`${localeKey}.document_signed_title`)}
        </Typography.Title>
        <Typography.Text
          style={{
            fontSize: 16,
            marginBottom: 24,
            display: "block",
            color: "var(--color-grey)",
            textAlign: "center",
          }}
        >
          {t(`${localeKey}.document_signed_description`)}
        </Typography.Text>

        {!withoutGoToMain && (
          <Button
            type="primary"
            onClick={() => navigate(customRout ? customRout : ROUTES.MAIN)}
          >
            {customRout
              ? t("personal_data.next")
              : t(`${localeKey}.go_to_main`)}
          </Button>
        )}
      </div>
    );
  }

  if (
    signDocumentData?.success &&
    document.sign_provider === "SMART_BRIDGE" &&
    isShowQrCode
  ) {
    return (
      <div className={styles.qrWrapper}>
        <Typography.Title level={2} style={{ textAlign: "center" }}>
          Отсканируйте qrCode
        </Typography.Title>
        <div
          dangerouslySetInnerHTML={{ __html: signDocumentData.qrCode || "" }}
        />

        <Button
          block
          onClick={() => {
            setShouldPoll(true);
            window.open(signDocumentData.link, "_blank");
          }}
          style={{ marginTop: 12, marginBottom: 8 }}
        >
          Открыть ссылку
        </Button>
      </div>
    );
  }

  if (signDocumentData?.success && document.sign_provider === "SMART_BRIDGE") {
    return (
      <div className={styles.checkSignSuccess}>
        <Spin />
      </div>
    );
  }

  if (signDocumentData?.success) {
    return (
      <>
        {userData && t(`${localeKey}.enter_sms_code`)}
        <Input
          mask="999999"
          name="additional-code"
          type="tel"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ textAlign: "center", marginBottom: 4 }}
        />
        {userData && t(`${localeKey}.code_sent_to`, { phone: userData.phone })}

        <Button
          block
          type={!isTimerActive ? "link" : "text"}
          onClick={() =>
            signDocument({
              id: document.id,
              params:
                document.sign_provider === "SMART_BRIDGE"
                  ? { provider: "SMART_BRIDGE" }
                  : undefined,
            })
          }
          disabled={isTimerActive}
          style={{ marginTop: 12, marginBottom: 8 }}
        >
          {isTimerActive
            ? t(`${localeKey}.resend_timer`, { seconds: timer })
            : t(`${localeKey}.resend_code`)}
        </Button>

        <Button
          type="primary"
          block
          onClick={() =>
            checkSignDocument({
              id: document.id,
              params: { token: signDocumentData.token!, code },
            })
          }
          loading={isCheckPending}
          style={{ marginTop: 16 }}
        >
          {t(`${localeKey}.sign_button`)}
        </Button>
      </>
    );
  }

  if (signDocumentData?.success === false || signDocumentIsError) {
    return (
      <Empty
        description={
          <Typography.Text>
            {(signDocumentError as any)?.response?.data?.message ||
              t(`${localeKey}.sms_error`)}
          </Typography.Text>
        }
      />
    );
  }

  return null;
};
